import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { Dayjs } from "dayjs";
import dayjs, { CHICAGO_TIMEZONE } from "../../utils/dayjsTimezone";
import axios from "axios";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import { OrientationDto } from "../../interfaces/Recruiting/orientation";

const isDateOnlyString = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);
const hasExplicitTimeZone = (value: string) =>
  /([zZ]|[+-]\d{2}:\d{2})$/.test(value);

const parseOrientationDateToChicago = (value: string): Dayjs => {
  if (!value) {
    return dayjs(new Date(NaN));
  }

  if (isDateOnlyString(value)) {
    return dayjs.tz(`${value}T00:00:00`, CHICAGO_TIMEZONE);
  }

  if (hasExplicitTimeZone(value)) {
    return dayjs(value).tz(CHICAGO_TIMEZONE);
  }

  return dayjs.tz(value, CHICAGO_TIMEZONE);
};

const getCalendarDateForOrientation = (orientation: OrientationDto): string =>
  orientation.orientationDate;

const fetchOrientations = async (
  token: string | null | undefined,
): Promise<OrientationDto[]> => {
  const { data } = await axios.get(`${apiVariable}/recruiting/orientations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const buildCalendar = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const endOfMonth = month.endOf("month");
  const daysInMonth = endOfMonth.date();
  const startWeekday = startOfMonth.day();

  const cells: Array<Dayjs | null> = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfMonth.date(day));
  }

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = remainder; i < 7; i += 1) {
      cells.push(null);
    }
  }

  return cells;
};

const WEEKDAY_LABELS = [
  { key: "sun", label: "S" },
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
];

const OrientationCalendarPage = () => {
  const { token } = useAuth();
  const initialMonth = dayjs().tz(CHICAGO_TIMEZONE).startOf("month");
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(initialMonth);
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);

  const {
    data: orientations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["orientations"],
    queryFn: () => fetchOrientations(token),
  });

  const orientationsWithDates = useMemo(
    () =>
      orientations
        .filter(
          (orientation) =>
            orientation.status?.toLowerCase() !== "declined" &&
            Boolean(orientation.orientationDate),
        )
        .map((orientation) => ({
          orientation,
          orientationDate: parseOrientationDateToChicago(
            getCalendarDateForOrientation(orientation),
          ),
        })),
    [orientations],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, { date: Dayjs; items: OrientationDto[] }>();
    orientationsWithDates.forEach(({ orientation, orientationDate }) => {
      if (!orientationDate.isValid()) {
        return;
      }
      const key = orientationDate.format("YYYY-MM-DD");
      const existing = map.get(key);
      const updatedOrientation = {
        ...orientation,
        orientationDate: orientationDate.toISOString(),
      };
      if (existing) {
        existing.items.push(updatedOrientation);
        existing.items.sort((a, b) =>
          dayjs(a.orientationDate).isAfter(dayjs(b.orientationDate)) ? 1 : -1,
        );
      } else {
        map.set(key, {
          date: orientationDate.startOf("day"),
          items: [updatedOrientation],
        });
      }
    });
    return map;
  }, [orientationsWithDates]);

  const calendarCells = useMemo(
    () => buildCalendar(currentMonth),
    [currentMonth],
  );

  useEffect(() => {
    if (selectedDay) {
      return;
    }
    const firstMatch = orientationsWithDates.find(({ orientationDate }) =>
      orientationDate.isSame(currentMonth, "month"),
    );
    if (firstMatch) {
      setSelectedDay(firstMatch.orientationDate.startOf("day"));
    }
  }, [currentMonth, orientationsWithDates, selectedDay]);

  const selectedKey = selectedDay?.format("YYYY-MM-DD") ?? "";
  const selectedItems = selectedKey
    ? (eventsByDay.get(selectedKey)?.items ?? [])
    : [];

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: CHICAGO_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
    setSelectedDay(null);
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (value) {
      setCurrentMonth(value.startOf("month"));
      setSelectedDay(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 0, sm: 3 } }}>
        <Typography color="error">
          {(error as Error).message || "Failed to load orientations."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 } }}>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <Button variant="outlined" onClick={handlePrevMonth}>
          Previous
        </Button>
        <Typography variant="h5" sx={{ minWidth: 200 }} align="center">
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        <Button variant="outlined" onClick={handleNextMonth}>
          Next
        </Button>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={["year", "month"]}
            label="Jump to"
            value={currentMonth}
            onChange={handleDateChange}
          />
        </LocalizationProvider>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={7}>
          <Paper sx={{ p: { xs: 0.5, sm: 2 } }}>
            <Grid container columns={7} spacing={{ xs: 0.25, sm: 1 }} mb={1}>
              {WEEKDAY_LABELS.map(({ key, label }) => (
                <Grid item xs={1} key={key}>
                  <Typography variant="subtitle2" align="center">
                    {label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Grid container columns={7} spacing={{ xs: 0.25, sm: 1 }}>
              {calendarCells.map((cell, index) => {
                if (!cell) {
                  return <Grid item xs={1} key={`empty-${index}`} />;
                }
                const key = cell.format("YYYY-MM-DD");
                const itemsForDay = eventsByDay.get(key)?.items ?? [];
                const isSelected = selectedDay?.isSame(cell, "day");
                return (
                  <Grid item xs={1} key={key}>
                    <Paper
                      variant={isSelected ? "outlined" : undefined}
                      sx={{
                        p: { xs: 0, sm: 1 },
                        minHeight: 44,
                        cursor: itemsForDay.length ? "pointer" : "default",
                        backgroundColor: itemsForDay.length
                          ? "rgba(25, 118, 210, 0.08)"
                          : "inherit",
                        borderColor: isSelected ? "primary.main" : undefined,
                      }}
                      onClick={() => {
                        if (itemsForDay.length) {
                          setSelectedDay(cell);
                        }
                      }}
                    >
                      <Stack alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle1">
                          {cell.date()}
                        </Typography>
                        {itemsForDay.length > 0 && (
                          <Chip
                            color="primary"
                            size="small"
                            label={
                              <>
                                {itemsForDay.length}
                                <Box
                                  component="span"
                                  sx={{ display: { xs: "none", sm: "inline" } }}
                                >
                                  {" "}
                                  scheduled
                                </Box>
                              </>
                            }
                          />
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4} lg={5}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {selectedDay
                ? `Orientations for ${selectedDay.format("MMMM D, YYYY")}`
                : "Select a day to view orientations"}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {selectedDay && selectedItems.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No orientations scheduled for this day.
              </Typography>
            )}
            {selectedDay && selectedItems.length > 0 && (
              <List>
                {selectedItems.map((item) => {
                  const time = formatter.format(new Date(item.orientationDate));
                  return (
                    <ListItem key={item.id} sx={{ alignItems: "flex-start" }}>
                      <ListItemText
                        primary={`${time} – ${item.driverFirstName} ${item.driverLastName}`}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              {item.company} · {item.status}
                            </Typography>
                            {item.recruiter && (
                              <>
                                <br />
                                Recruiter: {item.recruiter}
                              </>
                            )}
                            {item.division && (
                              <>
                                <br />
                                Division: {item.division}
                              </>
                            )}
                            {item.primaryPhoneNumber && (
                              <>
                                <br />
                                Phone: {item.primaryPhoneNumber}
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
            {!selectedDay && (
              <Typography variant="body2" color="textSecondary">
                Use the calendar to choose a day and review upcoming
                orientations.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrientationCalendarPage;
